import { ORDER_STATUSES, type OrderStatus, TRANSITION_ACTOR } from '@ambernord/shared';
import { type StepDef, Stepper } from '@ambernord/ui';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../lib/format.js';
import type { SupplyOrderDto } from './types.js';

/**
 * Dzīves cikla stepperis: ORDER_STATUSES (bez `cancelled`) → StepDef.
 * Kad pasūtījums jau ir ≥ `placed`, 4 pre-placed piedāvājuma soļi tiek izlaisti,
 * lai josla paliktu lasāma. Meta rāda soļa laikspiedolu (ja API to atdod) un
 * atbildīgo aktoru no TRANSITION_ACTOR.
 */
export function LifecycleStepper({ so }: { so: SupplyOrderDto }) {
  const { t, i18n } = useTranslation(['sodetail', 'statuses']);

  const flow: OrderStatus[] = ORDER_STATUSES.filter((s) => s !== 'cancelled');
  const currentIdx = flow.indexOf(so.status);
  const placedIdx = flow.indexOf('placed');
  const visible = currentIdx >= placedIdx ? flow.slice(placedIdx) : flow;

  // Soļa laikspiedoli, kā tos atdod API (piegādātāja projekcijā daļas nav — meta tad rāda tikai aktoru).
  const stamps: Partial<Record<OrderStatus, string | null | undefined>> = {
    confirmed: so.confirmedAt,
    labelled: so.labelledAt,
    loaded: so.loadedAt,
    dispatched: so.dispatchedAt,
    delivered: so.deliveredAt,
    inspected: so.inspectedAt,
    invoiced: so.invoicedAt,
    paid: so.paidAt,
  };

  const steps: StepDef[] = visible.map((status) => {
    const idx = flow.indexOf(status);
    const state: StepDef['state'] =
      idx < currentIdx ? 'done' : idx === currentIdx ? 'current' : 'upcoming';
    const stamp = stamps[status];
    const actor = t(`sodetail:actors.${TRANSITION_ACTOR[status]}`);
    const meta = stamp ? `${formatDateTime(stamp, i18n.language)} · ${actor}` : actor;
    return { key: status, label: t(`statuses:${status}`), meta, state };
  });

  return <Stepper steps={steps} />;
}
